import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import type { Message } from '@/types/messages.ts';
import type { Transcript } from './useTranscript.ts';

/** How close to the top counts as reaching it, in pixels. */
const NEAR_TOP_PX = 100;

export interface TranscriptScroll {
  /** The transcript's own box; on a phone the page scrolls instead. */
  scroller: RefObject<HTMLDivElement | null>;
  /** Everything drawn in the transcript, whose growth keeps an opened transcript at its end. */
  content: RefObject<HTMLDivElement | null>;
  /** Opens at the end and stays there while pictures and cards finish drawing, until the reader scrolls. */
  scrollToEnd: () => void;
  /** Reads one older page, keeping the reader on the words they were looking at. */
  readOlder: () => void;
  onScroll: () => void;
}

/**
 * Scrolling through a transcript that is read backwards a page at a time: an
 * older page loads when the reader nears the top, and lands without moving
 * what is on screen.
 */
export function useTranscriptScroll(
  transcript: Transcript,
  shown: Message[],
  belowMobile: boolean,
): TranscriptScroll {
  const { loading, loadingMore, hasMore, loadMore, merged } = transcript;
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  /* Whether the transcript was put at its end and the reader has not moved it since. */
  const keepAtEnd = useRef(false);
  /* The height the transcript had just before an older page was put on top of it. */
  const heightBeforeOlder = useRef<number | null>(null);
  const oldest = shown[0]?.id;

  /* On a phone the transcript is a screen of its own, so the page is what scrolls. */
  const scrollBox = useCallback(
    (): HTMLElement | null => (belowMobile ? document.documentElement : scroller.current),
    [belowMobile],
  );

  const toEnd = useCallback(() => {
    const element = scrollBox();
    if (element) element.scrollTop = element.scrollHeight;
  }, [scrollBox]);

  const scrollToEnd = useCallback(() => {
    keepAtEnd.current = true;
    toEnd();
  }, [toEnd]);

  /* Another transcript opens where its own opening puts it. */
  useEffect(() => {
    if (loading) keepAtEnd.current = false;
  }, [loading]);

  /*
   * Pictures and Teams cards finish drawing after the transcript was put at its
   * end, and push the end out of sight. Follow the growth until the reader acts.
   */
  useEffect(() => {
    const element = content.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      if (keepAtEnd.current) toEnd();
    });
    observer.observe(element);

    const leave = () => {
      keepAtEnd.current = false;
    };
    const events = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
    for (const event of events) window.addEventListener(event, leave, { passive: true });
    return () => {
      observer.disconnect();
      for (const event of events) window.removeEventListener(event, leave);
    };
  }, [toEnd]);

  const readOlder = useCallback(() => {
    const element = scrollBox();
    if (!element) return;

    heightBeforeOlder.current = element.scrollHeight;
    loadMore();
  }, [scrollBox, loadMore]);

  /*
   * An older page lands above what is on screen and would push it down by
   * exactly its own height. Putting that height back leaves the reader looking
   * at the same words.
   */
  useLayoutEffect(() => {
    const element = scrollBox();
    const before = heightBeforeOlder.current;
    if (!element || before === null) return;

    element.scrollTop += element.scrollHeight - before;
    heightBeforeOlder.current = null;
  }, [oldest, scrollBox]);

  /*
   * A channel whose newest conversation is three messages long leaves nothing
   * to scroll, and infinite scrolling never starts. Keep reading back until
   * there is.
   */
  useEffect(() => {
    if (!merged || !hasMore || loading || loadingMore) return;

    const element = scrollBox();
    if (!element || element.scrollHeight > element.clientHeight) return;

    /* Read as an older page: the reader stays at the end rather than at the top. */
    readOlder();
  }, [merged, hasMore, loading, loadingMore, readOlder, shown, scrollBox]);

  const onScroll = useCallback(() => {
    const transcriptBox = scroller.current;
    if (!transcriptBox || loadingMore || !hasMore) return;

    /* How far the reader is into the transcript, whichever box is scrolling it. */
    const readInto = belowMobile
      ? -transcriptBox.getBoundingClientRect().top
      : transcriptBox.scrollTop;
    if (readInto >= NEAR_TOP_PX) return;

    readOlder();
  }, [belowMobile, loadingMore, hasMore, readOlder]);

  useEffect(() => {
    if (!belowMobile) return;

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [belowMobile, onScroll]);

  return { scroller, content, scrollToEnd, readOlder, onScroll };
}
